import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import apiClient, { formatApiError } from "@/api/client";
import {
  ChevronLeft, Save, Eye, FileText, Send, Loader2, Plus, Trash2, ListTree, Sparkles,
  AlertCircle, CheckCircle2, ExternalLink, RefreshCw, X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const ACCENT_COLORS = ["#4F46E5", "#7C3AED", "#0EA5E9", "#16A34A", "#D97706", "#DC2626"];

export default function ReportBuilderPage() {
  const { reportId } = useParams();
  const nav = useNavigate();
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [keywordLists, setKeywordLists] = useState([]);
  const [adCopySets, setAdCopySets] = useState([]);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [previewKey, setPreviewKey] = useState(0); // bump to force iframe reload
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`/client-reports/${reportId}`);
      setReport(data);
      const [kl, ad] = await Promise.all([
        apiClient.get("/keyword-lists", { params: { customer_id: data.customer_id } }),
        apiClient.get("/ad-copy-sets", { params: { customer_id: data.customer_id } }),
      ]);
      setKeywordLists(kl.data || []);
      setAdCopySets(ad.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [reportId]); // eslint-disable-line

  if (!report) return <div className="p-10 text-center text-sm text-[#94A3B8]"><Loader2 size={16} className="animate-spin inline mr-2"/>Yükleniyor…</div>;

  const upd = (patch) => setReport({...report, ...patch});

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put(`/client-reports/${reportId}`, {
        name: report.name, subtitle: report.subtitle, summary: report.summary,
        keyword_list_ids: report.keyword_list_ids, ad_copy_set_ids: report.ad_copy_set_ids,
        custom_sections: report.custom_sections,
        include_negatives: report.include_negatives, include_match_types: report.include_match_types,
        include_rationales: report.include_rationales, accent_color: report.accent_color,
      });
      setReport(data); setPreviewKey(k=>k+1);
      toast.success("Kaydedildi");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishConfirmOpen(false);
    setPublishing(true);
    try {
      // Always save current edits first
      await apiClient.put(`/client-reports/${reportId}`, {
        name: report.name, subtitle: report.subtitle, summary: report.summary,
        keyword_list_ids: report.keyword_list_ids, ad_copy_set_ids: report.ad_copy_set_ids,
        custom_sections: report.custom_sections,
        include_negatives: report.include_negatives, include_match_types: report.include_match_types,
        include_rationales: report.include_rationales, accent_color: report.accent_color,
      });
      const { data } = await apiClient.post(`/client-reports/${reportId}/publish`);
      toast.success(data.email?.sent ? `Yayınlandı · ${data.email.to} adresine e-posta gönderildi` : "PDF oluşturuldu");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishing(false); }
  };

  const toggleList = (id) => {
    const cur = report.keyword_list_ids || [];
    upd({ keyword_list_ids: cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id] });
  };
  const toggleAdSet = (id) => {
    const cur = report.ad_copy_set_ids || [];
    upd({ ad_copy_set_ids: cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id] });
  };

  const addCustomSection = () => upd({ custom_sections: [...(report.custom_sections || []), { title: "Yeni Bölüm", body_html: "" }] });
  const updateCustom = (idx, patch) => {
    const arr = [...(report.custom_sections || [])];
    arr[idx] = { ...arr[idx], ...patch };
    upd({ custom_sections: arr });
  };
  const removeCustom = (idx) => upd({ custom_sections: (report.custom_sections || []).filter((_, i) => i !== idx) });

  const API = process.env.REACT_APP_BACKEND_URL;
  const authToken = token || (typeof window !== "undefined" ? localStorage.getItem("crm_token") : null);
  const previewUrl = `${API}/api/client-reports/${reportId}/preview?_=${previewKey}`;
  const pdfUrl = `${API}/api/client-reports/${reportId}/pdf?auth=${encodeURIComponent(authToken || "")}`;

  return (
    <div className="space-y-5" data-testid="report-builder">
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <Link to="/musteriler" className="hover:text-[#4F46E5] flex items-center gap-1"><ChevronLeft size={14}/>Müşteriler</Link>
        <span>/</span>
        <Link to={`/musteriler/${report.customer_id}?tab=reports`} className="hover:text-[#4F46E5]">Müşteri</Link>
        <span>/</span>
        <span className="font-medium text-[#0F172A]">{report.name}</span>
      </div>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText size={20} className="text-[#4F46E5]"/>{report.name}</h1>
          <div className="text-xs text-[#64748B] mt-1">
            Durum: <span className={`ky-badge text-[10px] ${report.status==="published"?"ky-badge-success":"ky-badge-neutral"}`}>{report.status === "published" ? "Yayınlandı" : "Taslak"}</span>
            {report.published_at && ` · son yayın ${new Date(report.published_at).toLocaleString("tr-TR")}`}
            {report.email_sent_at && ` · e-posta gönderildi`}
          </div>
        </div>
        <div className="flex gap-2">
          {report.status === "published" && (
            <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="ky-btn ky-btn-outline ky-btn-sm" data-testid="open-pdf"><ExternalLink size={12}/>Yayınlı PDF</a>
          )}
          <button onClick={save} disabled={saving} className="ky-btn ky-btn-outline" data-testid="save-btn">
            {saving ? <><Loader2 size={13} className="animate-spin"/>Kaydediliyor…</> : <><Save size={13}/>Kaydet</>}
          </button>
          <button onClick={()=>setPublishConfirmOpen(true)} disabled={publishing} className="ky-btn ky-btn-accent" data-testid="publish-btn">
            {publishing ? <><Loader2 size={13} className="animate-spin"/>Yayınlanıyor…</> : <><Send size={13}/>PDF Oluştur & E-posta Gönder</>}
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* LEFT — editor */}
        <div className="space-y-4">
          <div className="ky-card p-4 space-y-3">
            <h3 className="font-semibold text-sm">Rapor Başlığı</h3>
            <input value={report.name} onChange={e=>upd({name:e.target.value})} className="ky-input" placeholder="Rapor adı" data-testid="report-name"/>
            <input value={report.subtitle || ""} onChange={e=>upd({subtitle:e.target.value})} className="ky-input" placeholder="Alt başlık (örn: Mayıs 2026 Stratejisi)"/>
            <div>
              <label className="block text-xs text-[#64748B] mb-1.5">Renk Teması</label>
              <div className="flex gap-2">
                {ACCENT_COLORS.map(c => (
                  <button key={c} onClick={()=>upd({accent_color:c})} className={`w-7 h-7 rounded-full border-2 ${report.accent_color===c?"border-[#0F172A]":"border-[#ECECEF]"}`} style={{background:c}} title={c}/>
                ))}
              </div>
            </div>
          </div>

          <div className="ky-card p-4 space-y-3">
            <h3 className="font-semibold text-sm">Yönetici Özeti</h3>
            <textarea value={report.summary || ""} onChange={e=>upd({summary:e.target.value})} rows={4} placeholder="Bu raporun temel bulguları ve önerileri…" className="ky-textarea text-sm" data-testid="summary"/>
          </div>

          <div className="ky-card p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><ListTree size={14}/>Anahtar Kelime Listeleri ({(report.keyword_list_ids||[]).length} seçili)</h3>
            {keywordLists.length === 0 && <p className="text-xs text-[#94A3B8]">Bu müşteriye atanmış kayıtlı liste yok.</p>}
            <div className="space-y-1.5">
              {keywordLists.map(l => {
                const sel = (report.keyword_list_ids||[]).includes(l.id);
                return (
                  <label key={l.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${sel?"bg-[#EEF2FF]":"hover:bg-[#FAFAFB]"}`}>
                    <input type="checkbox" checked={sel} onChange={()=>toggleList(l.id)} className="w-4 h-4 accent-[#4F46E5]" data-testid={`list-${l.id}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{l.name}</div>
                      <div className="text-[10px] text-[#94A3B8]">{(l.positives||[]).length} pozitif · {(l.negatives||[]).length} negatif</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="ky-card p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2"><Sparkles size={14}/>Reklam Metni Setleri ({(report.ad_copy_set_ids||[]).length} seçili)</h3>
            {adCopySets.length === 0 && <p className="text-xs text-[#94A3B8]">Bu müşteriye atanmış kayıtlı set yok.</p>}
            <div className="space-y-1.5">
              {adCopySets.map(s => {
                const sel = (report.ad_copy_set_ids||[]).includes(s.id);
                return (
                  <label key={s.id} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${sel?"bg-[#FEF3C7]":"hover:bg-[#FAFAFB]"}`}>
                    <input type="checkbox" checked={sel} onChange={()=>toggleAdSet(s.id)} className="w-4 h-4 accent-[#D97706]" data-testid={`set-${s.id}`}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{s.name}</div>
                      <div className="text-[10px] text-[#94A3B8]">{(s.headlines||[]).length} başlık · {(s.descriptions||[]).length} açıklama</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="ky-card p-4 space-y-2">
            <h3 className="font-semibold text-sm">Gösterim Tercihleri</h3>
            <Toggle label="Negatif kelimeleri PDF'e dahil et" v={report.include_negatives !== false} on={(v)=>upd({include_negatives:v})}/>
            <Toggle label={'Eşleşme türlerini göster ([tam] / "sıralı" / geniş)'} v={report.include_match_types !== false} on={(v)=>upd({include_match_types:v})}/>
            <Toggle label="Her kelime için AI gerekçesini göster" v={report.include_rationales === true} on={(v)=>upd({include_rationales:v})}/>
          </div>

          <div className="ky-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-sm">Özel Bölümler</h3>
              <button onClick={addCustomSection} className="ky-btn ky-btn-outline ky-btn-sm" data-testid="add-section"><Plus size={11}/>Yeni Bölüm</button>
            </div>
            <div className="space-y-3">
              {(report.custom_sections || []).map((s, i) => (
                <div key={i} className="border border-[#ECECEF] rounded-lg p-3 space-y-2 bg-[#FAFAFB]">
                  <div className="flex gap-2">
                    <input value={s.title} onChange={e=>updateCustom(i, {title:e.target.value})} placeholder="Bölüm başlığı" className="ky-input ky-input-sm font-medium flex-1"/>
                    <button onClick={()=>removeCustom(i)} className="ky-btn ky-btn-ghost ky-btn-icon text-[#EF4444]"><Trash2 size={12}/></button>
                  </div>
                  <textarea value={s.body_html} onChange={e=>updateCustom(i, {body_html:e.target.value})} rows={4} placeholder="HTML destekli içerik — <p>, <strong>, <ul> kullanabilirsiniz." className="ky-textarea text-xs font-mono"/>
                </div>
              ))}
              {(report.custom_sections || []).length === 0 && <p className="text-xs text-[#94A3B8]">Henüz özel bölüm yok.</p>}
            </div>
          </div>
        </div>

        {/* RIGHT — preview */}
        <div className="space-y-4 lg:sticky lg:top-4 self-start" style={{ maxHeight: "calc(100vh - 100px)" }}>
          <div className="ky-card p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold"><Eye size={14}/>Canlı Önizleme</div>
            <button onClick={()=>{ save(); }} className="ky-btn ky-btn-outline ky-btn-sm"><RefreshCw size={11}/>Yenile</button>
          </div>
          <div className="ky-card overflow-hidden p-0">
            {authToken ? (
              <iframe
                key={previewKey}
                src={previewUrl + `&auth=${encodeURIComponent(authToken)}`}
                title="Rapor Önizleme"
                className="w-full bg-white"
                style={{ height: "70vh", border: "none" }}
                sandbox="allow-same-origin"
              />
            ) : (
              <div className="w-full bg-white flex items-center justify-center text-xs text-[#94A3B8]" style={{ height: "70vh" }}>
                <Loader2 size={14} className="animate-spin mr-2"/>Önizleme hazırlanıyor…
              </div>
            )}
          </div>
          <div className="text-[10px] text-[#94A3B8] flex items-start gap-1.5">
            <AlertCircle size={11} className="mt-0.5 shrink-0"/>
            <div>Önizleme HTML, PDF değildir. Değişiklikleri görmek için önce "Kaydet" tuşuna bas.</div>
          </div>
        </div>
      </div>

      {/* Publish confirmation dialog */}
      {publishConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={()=>setPublishConfirmOpen(false)} data-testid="publish-confirm-overlay">
          <div onClick={e=>e.stopPropagation()} className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 ky-fade-in">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#EEF2FF] text-[#4F46E5] flex items-center justify-center shrink-0">
                <Send size={18}/>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-[#0F172A] text-base">Raporu yayınla?</h3>
                <p className="text-sm text-[#64748B] mt-1">PDF oluşturulacak, müşteri portalında görünür hale gelecek ve müşteriye otomatik bir bilgilendirme e-postası gönderilecek.</p>
                <div className="mt-3 bg-[#FAFAFB] border border-[#ECECEF] rounded-lg p-3 text-[11px] text-[#475569] space-y-1">
                  <div><strong>Rapor:</strong> {report.name}</div>
                  <div><strong>Anahtar kelime listesi:</strong> {(report.keyword_list_ids||[]).length} seçili</div>
                  <div><strong>Reklam metni seti:</strong> {(report.ad_copy_set_ids||[]).length} seçili</div>
                </div>
              </div>
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={()=>setPublishConfirmOpen(false)} className="ky-btn ky-btn-outline" data-testid="publish-cancel">İptal</button>
              <button onClick={publish} className="ky-btn ky-btn-accent" data-testid="publish-confirm"><Send size={12}/>Yayınla ve E-posta Gönder</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Toggle = ({ label, v, on }) => (
  <label className="flex items-center justify-between gap-3 cursor-pointer text-xs py-1">
    <span>{label}</span>
    <button type="button" onClick={()=>on(!v)} className={`relative w-9 h-5 rounded-full transition-colors ${v?"bg-[#4F46E5]":"bg-[#E5E7EB]"}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${v?"left-[18px]":"left-0.5"}`}/>
    </button>
  </label>
);
